import { Trans } from '@lingui/macro'
import {
  Divider,
  List as MuiList,
  Stack,
  Typography,
  listItemAvatarClasses,
  listItemClasses,
  listItemSecondaryActionClasses,
  listItemTextClasses,
  styled,
  typographyClasses,
} from '@mui/material'
import { type EnhancedDomain, getPriceString } from 'jms-domain'

import { useResolveLocalizable, useResourceUrl } from '@salsita-ai/platform-project-sdk/react'
import { PropertyContainer, SummaryItem } from '@salsita-ai/platform-project-sdk/ui'

const PropertyContainerItem = styled(PropertyContainer.Item)(({ theme: { spacing } }) => ({
  [`.${typographyClasses.h6}`]: {
    paddingInline: spacing(1),
  },
}))

const List = styled(MuiList)(({ theme: { spacing } }) => ({
  display: 'grid',
  gap: spacing(0.5),
  margin: 0,
  paddingBlock: `${spacing(1.5)} ${spacing(2.5)}`,

  [`.${listItemClasses.root}`]: {
    gap: spacing(1.5),

    '&[class]': {
      padding: `${spacing(0.5)} ${spacing(1)}`,
    },
  },
  [`.${listItemAvatarClasses.root}`]: {
    minWidth: 0,
  },
  [`.${listItemTextClasses.root}`]: {
    margin: 0,
  },
  [`.${listItemSecondaryActionClasses.root}`]: {
    blockSize: '100%',

    '&[class][class]': {
      padding: 0,
    },
  },
}))

interface SummaryProps {
  summary: EnhancedDomain.$Room['$summary']
}

export const Summary = (props: SummaryProps) => {
  const { summary } = props

  const resolveLocalizable = useResolveLocalizable()
  const { getResourceUrl } = useResourceUrl()

  return (
    <>
      <Typography variant="h5">
        <Trans>Summary</Trans>
      </Typography>
      <Typography variant="body1">
        <Trans>Here's a summary of all the parts you've added to this room.</Trans>
      </Typography>

      <Divider />

      <Stack gap={2}>
        {summary.walls
          .filter(w => w.appliances.length > 0 || w.cabinets.length > 0 || w.parts.length > 0)
          .map(wall => (
            <PropertyContainerItem key={wall.id} title={resolveLocalizable(wall.name)}>
              <List>
                {wall.cabinets.map(item => (
                  <SummaryItem
                    key={item.id}
                    title={resolveLocalizable(item.name)}
                    subtitle={resolveLocalizable(item.sku)}
                    secondarySlot={
                      <Typography color="text.secondary">{getPriceString(item.currentUserPrice)}</Typography>
                    }
                    thumbnailUrl={item.thumbnailUrl ? getResourceUrl(item.thumbnailUrl) : undefined}
                  />
                ))}
                {wall.parts.map(item => (
                  <SummaryItem
                    key={item.id}
                    title={resolveLocalizable(item.name)}
                    subtitle={resolveLocalizable(item.sku)}
                    secondarySlot={
                      <Typography color="text.secondary">{getPriceString(item.currentUserPrice)}</Typography>
                    }
                  />
                ))}

                {wall.appliances.length > 0 && (
                  <>
                    <Divider sx={{ mb: 2, mt: 2 }} />

                    <Typography variant="body2" color="text.secondary">
                      <Trans>Appliances are sold separately</Trans>
                    </Typography>

                    {wall.appliances.map(item => (
                      <SummaryItem
                        key={item.id}
                        title={resolveLocalizable(item.name)}
                        subtitle={resolveLocalizable(item.sku)}
                        secondarySlot={
                          <Typography color="text.secondary">{getPriceString(item.currentUserPrice)}</Typography>
                        }
                        disableThumbnail
                      />
                    ))}
                  </>
                )}
              </List>
            </PropertyContainerItem>
          ))}
      </Stack>

      <Typography variant="body2" color="text.secondary">
        <Trans>Shipping costs are not included. They will be calculated and added to your final PDF quote.</Trans>
      </Typography>
    </>
  )
}
